/**
 * P-10 授权列表页。
 *
 * 装配：筛选栏（URL 为唯一真相）+ 表格（8 态图标 + --st-* token）+ 分页 + 键盘导航。
 * 严格处理五态：Loading / Error / 首次无数据 / 筛选无结果 / 有数据。
 * 「首次无数据」与「筛选无结果」是**两个不同分支**，后者不得出现「创建」按钮（§5.1）。
 *
 * 已知限制（非缺陷，已登记）：搜索框每键入一次即触发一次列表重载。
 * 当前由 mock 驱动（VISIBLE_MOCKS 默认开），真实后端接入后应在端点层加防抖，
 * 页面层不感知。本迭代不私自加防抖，避免与端点层职责重叠。
 */
import { useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ErrorBlock, EmptyFiltered, EmptyFirstRun, TableSkeleton } from '../../components/DataState';
import { Pager } from '../../components/Pager';
import { Icon } from '../../icons/Icon';
import { ICON_SIZE } from '../../tokens';
import { LicenseFilterBar } from './LicenseFilterBar';
import { LicenseTableBody, LicenseTableHead, LICENSE_COLUMN_COUNT } from './LicenseTable';
import { useLicenseQuery } from './useLicenseQuery';
import { useTableKeyboard } from '../../lib/useTableKeyboard';
import { useAsync } from '../../lib/useAsync';
import { describeError } from '../../api/client';
import { listCustomers, listLicenses, listProducts } from '../../api/endpoints';
import type { Customer, License, LicenseStatus, ListLicensesQuery, Product } from '../../api/types';

type SortKey = NonNullable<ListLicensesQuery['sort']>;

export function LicensesPage() {
  const navigate = useNavigate();
  const { filters, query, hasActiveFilters, setFilter, clearFilters } = useLicenseQuery();

  const licenses = useAsync(useCallback(() => listLicenses(query), [query]), [query]);
  const products = useAsync(useCallback(() => listProducts(), []), []);
  const customers = useAsync(useCallback(() => listCustomers(), []), []);

  const productList: readonly Product[] = products.state.kind === 'ready' ? products.state.data.items : [];
  const customerList: readonly Customer[] = customers.state.kind === 'ready' ? customers.state.data.items : [];

  // 状态计数来自当前结果集：mock 无全局计数端点，默认每页 50 已覆盖 12 条种子数据，
  // 真实后端接入后此处应改为读取契约里的聚合字段（若存在），不在前端伪造。
  const statusCounts = useMemo<Partial<Record<LicenseStatus, number>>>(() => {
    if (licenses.state.kind !== 'ready') return {};
    const counts: Partial<Record<LicenseStatus, number>> = {};
    for (const item of licenses.state.data.items) {
      counts[item.status] = (counts[item.status] ?? 0) + 1;
    }
    return counts;
  }, [licenses.state]);

  const searchRef = useRef<HTMLInputElement | null>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<readonly License[]>([]);
  if (licenses.state.kind === 'ready') {
    itemsRef.current = licenses.state.data.items;
  }

  const onActivate = useCallback(
    (index: number) => {
      const item = itemsRef.current[index];
      if (item !== undefined) navigate(`/licenses/${item.id}`);
    },
    [navigate],
  );

  const keyboardEnabled = licenses.state.kind === 'ready' && licenses.state.data.items.length > 0;
  useTableKeyboard({
    containerRef: tableRef,
    searchRef,
    rowSelector: '[data-license-row]',
    onActivate,
    enabled: keyboardEnabled,
  });

  const onSort = useCallback(
    (sort: SortKey) => {
      const nextOrder: 'asc' | 'desc' =
        filters.sort === sort && filters.order === 'desc' ? 'asc' : 'desc';
      setFilter({ sort, order: nextOrder });
    },
    [filters.sort, filters.order, setFilter],
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">授权</h1>
          <p className="page-head__sub">
            已兑换的正式授权与其生命周期状态（P-10）。撤销与暂停会改变吊销列表版本号，客户端下次校验即生效。
          </p>
        </div>
        <div className="page-head__actions">
          <Link to="/licenses/new" className="btn btn--primary">
            <Icon name="plus" size={ICON_SIZE.control} />
            新建授权
          </Link>
        </div>
      </div>

      <LicenseFilterBar
        ref={searchRef}
        filters={filters}
        products={productList}
        statusCounts={statusCounts}
        hasActiveFilters={hasActiveFilters}
        onChange={setFilter}
        onClear={clearFilters}
      />

      <div className="table-frame" ref={tableRef}>
        {licenses.state.kind === 'failed' ? (
          <ErrorBlock
            message={describeError(licenses.state.error, '加载授权列表')}
            onRetry={licenses.reload}
          />
        ) : licenses.state.kind === 'loading' ? (
          <table className="data-table">
            <LicenseTableHead filters={filters} onSort={onSort} />
            <TableSkeleton rows={Math.min(filters.limit, 12)} columns={LICENSE_COLUMN_COUNT} />
          </table>
        ) : licenses.state.data.items.length === 0 ? (
          hasActiveFilters ? (
            <EmptyFiltered
              description="没有符合当前筛选条件的授权。试试放宽状态或产品范围，或清除筛选。"
              onClearFilters={clearFilters}
            />
          ) : (
            <EmptyFirstRun
              icon="file-key"
              description="还没有任何已兑换的授权。授权由「授权码批次」生成并兑换后才会在这里出现（P-09 / P-10）。"
            />
          )
        ) : (
          <>
            <table className="data-table">
              <LicenseTableHead filters={filters} onSort={onSort} />
              <LicenseTableBody rows={licenses.state.data.items} products={productList} customers={customerList} />
            </table>
            <Pager
              page={filters.page}
              limit={filters.limit}
              total={licenses.state.data.total}
              hasMore={licenses.state.data.hasMore}
              onPageChange={(page) => setFilter({ page })}
              onLimitChange={(limit) => setFilter({ limit, page: 1 })}
            />
          </>
        )}
      </div>
    </>
  );
}
